import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    Users, Search, Download, Eye, Mail, Phone, MapPin,
    Calendar, ShoppingBag, DollarSign, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatCurrency, formatDate, getInitials } from '@utils';
import api from '@services/api';
import type { ListResponse } from '@types';

interface Customer {
    id: number;
    name: string;
    email: string;
    phone?: string;
    totalOrders: number;
    totalSpent: number;
    lastOrderAt?: string;
    createdAt: string;
}

export default function CustomersListPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const { data: customers, isLoading } = useQuery({
        queryKey: ['customers', currentPage, searchQuery],
        queryFn: () => api.get<ListResponse<Customer>>('/customers', {
            page: currentPage,
            search: searchQuery || undefined,
        }),
    });

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customers</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">View and manage your customer base</p>
                </div>
                <button className="btn-secondary">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            <div className="flex gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search customers by name or email..."
                        className="input pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="card overflow-hidden">
                {isLoading ? (
                    <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-500" /></div>
                ) : customers?.data?.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No customers found.</p>
                    </div>
                ) : (
                    <table className="table">
                        <thead className="table-header">
                            <tr>
                                <th className="table-header-cell">Customer</th>
                                <th className="table-header-cell">Contact</th>
                                <th className="table-header-cell">Orders</th>
                                <th className="table-header-cell">Total Spent</th>
                                <th className="table-header-cell">Last Active</th>
                                <th className="table-header-cell">Joined</th>
                                <th className="table-header-cell">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {customers?.data?.map((customer) => (
                                <tr key={customer.id} className="table-row">
                                    <td className="table-cell">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-medium text-sm">
                                                {getInitials(customer.name)}
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white">{customer.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="table-cell">
                                        <div className="text-sm space-y-1">
                                            <div className="flex items-center gap-2 text-gray-500">
                                                <Mail className="w-3 h-3" /> {customer.email}
                                            </div>
                                            {customer.phone && (
                                                <div className="flex items-center gap-2 text-gray-500">
                                                    <Phone className="w-3 h-3" /> {customer.phone}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="table-cell font-medium">
                                        <div className="flex items-center gap-1">
                                            <ShoppingBag className="w-3 h-3 text-gray-400" />
                                            {customer.totalOrders}
                                        </div>
                                    </td>
                                    <td className="table-cell font-medium text-success-600">
                                        {formatCurrency(customer.totalSpent)}
                                    </td>
                                    <td className="table-cell text-sm text-gray-500">
                                        {customer.lastOrderAt ? formatDate(customer.lastOrderAt) : 'Never'}
                                    </td>
                                    <td className="table-cell text-sm text-gray-500">
                                        {formatDate(customer.createdAt)}
                                    </td>
                                    <td className="table-cell">
                                        <Link to={`/customers/${customer.email}`} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg inline-flex text-gray-500 hover:text-primary-600">
                                            <Eye className="w-4 h-4" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {customers?.meta && customers.meta.lastPage > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Showing {(customers.meta.currentPage - 1) * customers.meta.perPage + 1} to{' '}
                        {Math.min(customers.meta.currentPage * customers.meta.perPage, customers.meta.total)} of{' '}
                        {customers.meta.total} customers
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="btn-secondary p-2"
                            aria-label="Previous Page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Page {currentPage} of {customers.meta.lastPage}
                        </span>
                        <button
                            onClick={() => setCurrentPage((p) => Math.min(customers.meta.lastPage, p + 1))}
                            disabled={currentPage === customers.meta.lastPage}
                            className="btn-secondary p-2"
                            aria-label="Next Page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </motion.div>
    );
}
